import { z } from 'zod4';

import { isoDatetimeToDate, isoDateToDate } from './dates';
import { fixture } from './fixtures';
import { RuleGroup, ruleGroupFixture } from './groups';
import { User, userFixture } from './users';

const NominationPeriodStatus = z.enum(['open', 'upcoming', 'closed']);

export const Nomination = z.object({
	id: z.string(),
	reason: z.string(),
	nominationPeriodId: z.string(),
	nominatorId: z.string(),
	nominatedUserId: z.string(),
	nominator: User,
	nominatedUser: User,
	createdAt: isoDatetimeToDate,
});

export type Nomination = z.infer<typeof Nomination>;

export const NominatedUserSummary = z.object({
	userId: z.string(),
	summary: z.string(),
	user: User,
});

export type NominatedUserSummary = z.infer<typeof NominatedUserSummary>;

export const AdminNominationPeriod = z.object({
	id: z.string(),
	name: z.string(),
	description: z.string(),
	startDate: isoDateToDate,
	endDate: isoDateToDate.nullable(),
	status: NominationPeriodStatus,
});

export type AdminNominationPeriod = z.infer<typeof AdminNominationPeriod>;

export const AdminNominationPeriodIndex = AdminNominationPeriod.extend({
	uniqueNominatedUserCount: z.number().int().nonnegative(),
	totalNominations: z.number().int().nonnegative(),
	creator: User.nullable(),
});

export type AdminNominationPeriodIndex = z.infer<typeof AdminNominationPeriodIndex>;

export const AdminNominationPeriodDetail = AdminNominationPeriod.extend({
	nominationLimit: z.number().nullable(),
	nominations: z.array(Nomination),
	nomineeRulesGroup: RuleGroup.nullable(),
	nominatorRulesGroup: RuleGroup.nullable(),
	favoritedUserIds: z.array(z.string()),
	nominatedUserSummaries: z.array(NominatedUserSummary),
	visibility: z.enum(['admin', 'public']),
});

export type AdminNominationPeriodDetail = z.infer<typeof AdminNominationPeriodDetail>;

const nominationLimitSchema = z.codec(z.number().nullable(), z.number().or(z.literal(Infinity)).nullable(), {
	decode: (value) => {
		return value === null ? Infinity : value;
	},
	encode: (value) => {
		return value === Infinity ? null : value;
	},
});

export const UserNominationPeriod = z.object({
	id: z.string(),
	name: z.string(),
	description: z.string(),
	nominationLimit: nominationLimitSchema,
	startDate: isoDateToDate,
	endDate: isoDateToDate.nullable(),
	status: NominationPeriodStatus,
	currentUserNominations: z.array(Nomination),
	nomineeRulesGroup: RuleGroup.nullable(),
});

export type UserNominationPeriod = z.infer<typeof UserNominationPeriod>;

export type NominationPeriodStatus = z.infer<typeof NominationPeriodStatus>;

// Utility functions to replicate getter behavior from original classes
export const getNominationPeriodDetailPath = (id: string): string => `/admin/programs/nominations/${id}`;

export const getNominationPeriodEditPath = (id: string): string => `/admin/programs/nominations/${id}/edit`;

export const isNominationPeriodUpcoming = (period: { status: NominationPeriodStatus }): boolean => period.status === 'upcoming';

export const isNominationPeriodClosed = (period: { status: NominationPeriodStatus }): boolean => period.status === 'closed';

export const isNominationPeriodOpen = (period: { status: NominationPeriodStatus }): boolean => period.status === 'open';

export const isNominationPeriodPublic = (period: { visibility: 'admin' | 'public' }): boolean => period.visibility === 'public';

export const canSubmitNomination = (period: UserNominationPeriod): boolean => {
	const limit = period.nominationLimit === null ? Infinity : period.nominationLimit;
	return limit === Infinity || period.currentUserNominations.length < limit;
};

// Fixture functions using manual generators
export const nominationFixture = (overrides: Partial<Nomination> = {}): Nomination => ({
	id: fixture.id(),
	reason: fixture.string('Outstanding work on'),
	nominationPeriodId: fixture.id(),
	nominatorId: fixture.id(),
	nominatedUserId: fixture.id(),
	nominator: userFixture(),
	nominatedUser: userFixture(),
	createdAt: fixture.date(),
	...overrides,
});

export const nominatedUserSummaryFixture = (overrides: Partial<NominatedUserSummary> = {}): NominatedUserSummary => ({
	userId: fixture.id(),
	summary: fixture.string('Summary for'),
	user: userFixture(),
	...overrides,
});

export const adminNominationPeriodFixture = (overrides: Partial<AdminNominationPeriod> = {}): AdminNominationPeriod => ({
	id: fixture.id(),
	name: fixture.name('Nomination Period'),
	description: fixture.description('nomination period'),
	startDate: fixture.pastDate(),
	endDate: fixture.futureDate(),
	status: 'open',
	...overrides,
});

export const adminNominationPeriodIndexFixture = (overrides: Partial<AdminNominationPeriodIndex> = {}): AdminNominationPeriodIndex => ({
	id: fixture.id(),
	name: fixture.name('Nomination Period'),
	description: fixture.description('nomination period'),
	startDate: fixture.pastDate(),
	endDate: fixture.futureDate(),
	uniqueNominatedUserCount: fixture.int(1, 20),
	totalNominations: fixture.int(1, 50),
	status: 'open',
	creator: userFixture(),
	...overrides,
});

export const adminNominationPeriodDetailFixture = (overrides: Partial<AdminNominationPeriodDetail> = {}): AdminNominationPeriodDetail => ({
	id: fixture.id(),
	name: fixture.name('Nomination Period'),
	description: fixture.description('nomination period'),
	startDate: fixture.pastDate(),
	endDate: fixture.futureDate(),
	status: 'open',
	nominationLimit: null,
	nominations: [nominationFixture(), nominationFixture()],
	nomineeRulesGroup: ruleGroupFixture(),
	nominatorRulesGroup: ruleGroupFixture(),
	favoritedUserIds: [],
	nominatedUserSummaries: [nominatedUserSummaryFixture()],
	visibility: 'admin',
	...overrides,
});

export const userNominationPeriodFixture = (overrides: Partial<UserNominationPeriod> = {}): UserNominationPeriod => ({
	id: fixture.id(),
	name: fixture.name('Nomination Period'),
	description: fixture.description('nomination period'),
	nominationLimit: 1,
	startDate: fixture.pastDate(),
	endDate: fixture.futureDate(),
	status: 'open',
	currentUserNominations: [nominationFixture()],
	nomineeRulesGroup: ruleGroupFixture(),
	...overrides,
});
